import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { blog } from '../data/mock';
import { ArrowLeft, Calendar, User, Tag } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const Blog = () => {
  const { t } = useLanguage();
  
  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section with B&W background, color subject */}
      <section
        className="relative h-96 flex items-center justify-center overflow-hidden"
      >
        {/* B&W Background Layer */}
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_11a5c023-0086-4456-b2f2-508c2ca946e7/artifacts/46fktsj9_DSCF4356.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center 40%',
            filter: 'grayscale(100%)'
          }}
        ></div>
        {/* Color Subject Overlay - same image without filter */}
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_11a5c023-0086-4456-b2f2-508c2ca946e7/artifacts/46fktsj9_DSCF4356.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center 40%',
            maskImage: 'radial-gradient(ellipse 45% 60% at 50% 45%, black 30%, transparent 70%)',
            WebkitMaskImage: 'radial-gradient(ellipse 45% 60% at 50% 45%, black 30%, transparent 70%)'
          }}
        ></div>
        <div className="absolute inset-0 bg-black/40"></div>
        {/* Vignette Effect */}
        <div 
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(ellipse at center, transparent 0%, rgba(0,0,0,0.3) 30%, rgba(0,0,0,0.8) 70%, rgba(0,0,0,0.95) 100%)'
          }}
        ></div>
        <div className="relative z-10 text-center text-white px-4 pt-16">
          <h1 className="text-5xl md:text-6xl font-black mb-4"><span className="text-cyan-400">{t('blogTitle')}</span></h1>
          <p className="text-xl md:text-2xl font-light text-zinc-300">{t('insightsFromArena')}</p>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="py-20 bg-zinc-950">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="space-y-12">
            {blog.map((post) => (
              <Link
                key={post.id}
                to={`/blog/${post.id}`}
                className="group block bg-zinc-900 border border-zinc-800 overflow-hidden hover:border-pink-500/50 transition-all duration-300"
              >
                <div className="md:flex">
                  <div className="md:w-2/5 h-64 md:h-auto overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="md:w-3/5 p-8">
                    <div className="flex items-center space-x-4 text-sm text-zinc-500 mb-3">
                      <span className="bg-pink-500/20 text-pink-400 px-3 py-1 rounded-full font-medium">
                        {post.category}
                      </span>
                      <span className="flex items-center">
                        <Calendar className="w-4 h-4 mr-1" />
                        {post.date}
                      </span>
                    </div>
                    <h2 className="text-2xl md:text-3xl font-bold mb-3 text-white group-hover:text-cyan-400 transition-colors">
                      {post.title}
                    </h2>
                    <p className="text-zinc-400 mb-4 line-clamp-3">{post.excerpt}</p>
                    <div className="flex items-center text-sm text-zinc-500">
                      <User className="w-4 h-4 mr-2" />
                      <span>{post.author}</span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export const BlogDetail = () => {
  const { id } = useParams();
  const post = blog.find((item) => item.id === parseInt(id));
  const { t } = useLanguage();

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">{t('postNotFound')}</h2>
          <Link to="/blog" className="text-gray-600 hover:text-gray-900">
            {t('backToBlog')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section
        className="relative h-96 md:h-[500px] flex items-end"
        style={{
          backgroundImage: `url(${post.image})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
        <div className="relative z-10 container mx-auto px-4 pb-12 text-white">
          <Link
            to="/blog"
            className="inline-flex items-center space-x-2 mb-6 text-white hover:text-gray-300 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>{t('backToBlog')}</span>
          </Link>
          <div className="flex items-center space-x-4 mb-4">
            <span className="bg-white text-gray-900 px-3 py-1 rounded-full text-sm font-semibold">
              {post.category}
            </span>
            <span className="flex items-center text-sm">
              <Calendar className="w-4 h-4 mr-2" />
              {post.date}
            </span>
            <span className="flex items-center text-sm">
              <User className="w-4 h-4 mr-2" />
              {post.author}
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold max-w-4xl">{post.title}</h1>
        </div>
      </section>

      {/* Post Content */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 max-w-3xl">
          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-700 leading-relaxed mb-8 font-light italic">
              {post.excerpt}
            </p>
            <div className="text-gray-700 leading-relaxed whitespace-pre-line">
              {post.content}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Blog;